import { Card, Flex, Typography } from 'antd';
import React, { useEffect, useState, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import Icon from '@ant-design/icons';
import { ReactComponent as MoreSvg } from '../../../../assets/icons/more.svg';
import { ReactComponent as MoreBlackSvg } from '../../../../assets/icons/more-black.svg';
import { useTheme } from '../../../../context/ThemeContext';
import {
  ANOMALY_TYPE,
  CHART_BG_COLOR,
  CHART_MARGINS,
  DARK,
} from '../../../../utilities/constants';
import Legend from './Legend';
import { SuspenseFallback } from '../../../../components/SuspenseFallback/SuspenseFallback';

// Lazy load the Plot component
const Plot = lazy(() => import('../../../../components/Plot/Plot'));

const { Text } = Typography;

const getResolvedColor = (variableName, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  return value || fallback;
};

const getAnomalyColorVar = (base, isSelected, isHov) => {
  if (isSelected) return `${base}-selected`;
  if (isHov) return `${base}-hovered`;
  return base;
};

const getAnomalyFallbackColor = (
  selectedVal,
  hoveredVal,
  defaultVal,
  isSelected,
  isHov
) => {
  if (isSelected) return selectedVal;
  if (isHov) return hoveredVal;
  return defaultVal;
};

const getMoreIcon = (isSelected, isDark) => {
  if (isSelected) return isDark ? MoreBlackSvg : MoreSvg;
  return isDark ? MoreSvg : MoreBlackSvg;
};

const getCardBackground = (isSelected, isDark) => {
  if (isSelected) return 'var(--selected-bg-color, #D0D4D8)';
  return isDark ? '#2E3033' : '#E0E2E5';
};

const AnomaliesCard = ({ selectedTabKey, setSelectedTabKey, anomalyData }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isSelected = selectedTabKey === 1;
  const isDark = theme === DARK;
  const selectedTextColor = isDark ? '#000000' : '#FFFFFF';
  const defaultTextColor = isDark ? '#FFFFFF' : '#000000';
  const chartTextColor = isSelected ? selectedTextColor : defaultTextColor;
  const [totalCount, setTotalCount] = useState(0);

  const criticalColor = getResolvedColor(
    getAnomalyColorVar('--driver-anomaly-color', isSelected, isHovered),
    getAnomalyFallbackColor(
      '#FF2124',
      '#FF6E6F',
      '#FF2124',
      isSelected,
      isHovered
    )
  );
  const majorColor = getResolvedColor(
    getAnomalyColorVar('--major-anomaly-color', isSelected, isHovered),
    getAnomalyFallbackColor(
      '#EEC100',
      '#AD8C00',
      '#EEC100',
      isSelected,
      isHovered
    )
  );
  const minorColor = getResolvedColor(
    getAnomalyColorVar('--minor-anomaly-color', isSelected, isHovered),
    getAnomalyFallbackColor(
      '#54ABA5',
      '#2E908B',
      '#419E98',
      isSelected,
      isHovered
    )
  );

  const [plotData, setPlotData] = useState([
    {
      values: [0, 0, 0],
      labels: [
        ANOMALY_TYPE.DRIVER_DROWSINESS,
        ANOMALY_TYPE.MAJOR,
        ANOMALY_TYPE.MINOR,
      ],
      type: 'pie',
      hole: 0.75,
      textinfo: 'value',
      textposition: 'outside',
      textfont: {
        size: 16,
        color: chartTextColor,
        weight: 700,
      },
      automargin: true,
      marker: {
        colors: [criticalColor, majorColor, minorColor],
      },
    },
  ]);

  const [layout, setLayout] = useState({
    showlegend: false,
    paper_bgcolor: CHART_BG_COLOR,
    plot_bgcolor: CHART_BG_COLOR,
    margin: CHART_MARGINS,
    height: 240,
    annotations: [
      {
        font: {
          color: chartTextColor,
          size: 32,
        },
        showarrow: false,
        text: `<b>${totalCount || 0}</b>`,
        x: 0.5,
        y: 0.5,
      },
    ],
  });

  useEffect(() => {
    setLayout(prev => ({
      ...prev,
      annotations: [
        {
          font: {
            color: chartTextColor,
            size: 32,
          },
          showarrow: false,
          text: `<b>${totalCount || 0}</b>`,
          x: 0.5,
          y: 0.5,
        },
      ],
    }));
  }, [totalCount, chartTextColor]);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(item => ({
        ...item,
        textfont: {
          ...item.textfont,
          color: chartTextColor,
        },
      }))
    );
  }, [setPlotData, chartTextColor]);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(item => ({
        ...item,
        marker: {
          ...item.marker,
          colors: [criticalColor, majorColor, minorColor],
        },
      }))
    );
  }, [criticalColor, majorColor, minorColor]);

  useEffect(() => {
    const driverCount =
      anomalyData?.[ANOMALY_TYPE.DRIVER_DROWSINESS]?.length || 0;
    const majorCount = anomalyData?.[ANOMALY_TYPE.MAJOR]?.length || 0;
    const minorCount = anomalyData?.[ANOMALY_TYPE.MINOR]?.length || 0;

    setPlotData(prevPlotData =>
      prevPlotData.map(plotItem => ({
        ...plotItem,
        values: [driverCount, majorCount, minorCount],
      }))
    );
    setTotalCount(driverCount + majorCount + minorCount);
  }, [anomalyData]);

  return (
    <Card
      size="small"
      variant="borderless"
      onClick={() => setSelectedTabKey(1)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`card-safety-anomaly-chart card-safety-anomaly-chart-${isSelected ? 'selected' : 'not-selected'}`}
      style={{
        background: getCardBackground(isSelected, isDark),
        height: '100%',
      }}
    >
      <Flex vertical gap="large" className="full-height">
        <Flex justify="space-between">
          <Text
            className="card-title"
            style={{
              color: isSelected ? selectedTextColor : defaultTextColor,
            }}
          >
            Anomalies
          </Text>
          <Icon
            component={getMoreIcon(isSelected, isDark)}
            className="card-title"
          />
        </Flex>
        <Flex
          vertical
          gap={20}
          align="center"
          justify="center"
          className="full-height"
        >
          <Suspense
            fallback={<SuspenseFallback height={'240px'} width={'100%'} />}
          >
            <Plot
              data={plotData}
              layout={layout}
              className="full-width"
              style={{
                marginTop: 0,
              }}
              config={{
                responsive: true,
                displayModeBar: false,
              }}
            />
          </Suspense>
          <Legend
            items={[
              {
                key: 'driver_drowsiness',
                label: ANOMALY_TYPE.DRIVER_ANOMALIES,
                color: criticalColor,
              },
              {
                key: 'major',
                label: ANOMALY_TYPE.MAJOR,
                color: majorColor,
              },
              {
                key: 'minor',
                label: ANOMALY_TYPE.MINOR,
                color: minorColor,
              },
            ]}
            color={isSelected ? selectedTextColor : defaultTextColor}
          />
        </Flex>
      </Flex>
    </Card>
  );
};

AnomaliesCard.propTypes = {
  selectedTabKey: PropTypes.number,
  setSelectedTabKey: PropTypes.func.isRequired,
  anomalyData: PropTypes.object,
};

export default React.memo(AnomaliesCard);
